/**
 * V2 auth — session handling on the staging subdomain.
 *
 * Design record: docs/AUTH-AND-SDK.md. The SDK is used ONLY for the OAuth
 * redirect flow and auth endpoints; localStorage is the source of truth
 * for session state, and all the iPad-Safari mitigations (navigator.locks
 * bypass, stored-session recovery, manual token refresh) live in the
 * shared gateway. This module's job is to put auth state in the V2 store
 * so views can subscribe to it: { authReady, user, profile }.
 *
 * The app never blocks on auth: browsing works logged-out, and works even
 * if the SDK script fails to load (raw REST reads the persisted session
 * straight from localStorage).
 */
import * as gateway from './gateway.js';
import { invalidate } from './query.js';
// Same adapter role as gateway.js: auth-diagnostics is shared with V1 by
// design — it is how auth is debugged on-device (?authdiag=1, no DevTools).
import * as diag from '../../assets/js/auth-diagnostics.js';
import { validatePassword } from '../../assets/js/utils.js';

// Pinned SDK version — keep in lockstep with yoink-page.php and
// assets/js/app.js (see docs/AUTH-AND-SDK.md "CDN version pinning").
const SDK_FALLBACK_URL =
  'https://unpkg.com/@supabase/supabase-js@2.108.1/dist/umd/supabase-js.min.js';

function sdkLoaded() {
  return Boolean(window.supabase?.createClient);
}

function waitForSdk(timeoutMs) {
  return new Promise((resolve) => {
    if (sdkLoaded()) return resolve(true);
    const started = Date.now();
    const timer = setInterval(() => {
      if (sdkLoaded()) {
        clearInterval(timer);
        resolve(true);
      } else if (Date.now() - started >= timeoutMs) {
        clearInterval(timer);
        resolve(false);
      }
    }, 100);
  });
}

/**
 * Wait for the SDK script from index.php; if it never arrives (content
 * blocker, CDN stall), inject the fallback CDN and wait once more.
 * @returns {Promise<boolean>} true if window.supabase is usable
 */
async function ensureSdk(timeoutMs = 10000) {
  if (await waitForSdk(timeoutMs)) return true;
  diag.record('sdk-timeout', 'primary CDN; trying fallback');
  if (!document.querySelector(`script[src="${SDK_FALLBACK_URL}"]`)) {
    const script = document.createElement('script');
    script.src = SDK_FALLBACK_URL;
    script.async = true;
    document.head.appendChild(script);
  }
  return waitForSdk(timeoutMs);
}

/**
 * Get the current session, working at every degradation level:
 * SDK healthy → SDK hung (gateway recovers from storage) → SDK script
 * never loaded (read/refresh the stored session directly).
 */
async function restoreSession() {
  if (gateway.isInitialized()) {
    const { data } = await gateway.getSession();
    return data?.session || null;
  }
  const stored = gateway.getStoredSession();
  if (!stored?.user) return null;
  if (!gateway.isSessionExpired(stored)) {
    diag.record('session-recovered', 'stored session without SDK');
    return stored;
  }
  return gateway.refreshStoredSession();
}

async function loadProfile(userId) {
  const { data, error } = await gateway.rawQuery(gateway.TABLES.PROFILES, {
    select: 'id,username,full_name,avatar_url,role,tutorial_progress',
    filters: { id: `eq.${userId}` },
    limit: 1,
  });
  if (error) throw error;
  return data?.[0] || null;
}

// The id whose profile fetch is in flight, so concurrent auth events
// (INITIAL_SESSION racing a TOKEN_REFRESHED) don't stack duplicate reads.
let profileInFlight = null;

/**
 * Put a session's user in the store and make sure a profile is on its way.
 *
 * The profile is fetched whenever the store doesn't already hold one for
 * this user — NOT once per sign-in. The old guard returned early on any
 * repeat event for the same id, so a first fetch that failed (an expired
 * token on an iPad wake, a dropped request) latched for the whole session:
 * `profile` stayed null, and the header showed the email handle until a
 * full reload. Retrying on the next auth event costs one cheap read.
 */
function applySession(store, session) {
  const previous = store.get().user;
  const sameUser = previous?.id === session.user.id;
  store.set({ user: session.user });

  if (!sameUser) {
    // Logged-in reads can return more under RLS than anonymous ones did
    invalidate();
    if (gateway.isInitialized()) gateway.rpc('update_last_login');
  }

  if (store.get().profile?.id === session.user.id) return;
  if (profileInFlight === session.user.id) return;
  profileInFlight = session.user.id;
  store.set({ profileLoading: true });

  loadProfile(session.user.id)
    .then((profile) => {
      // Ignore a stale fetch if the user changed/signed out meanwhile
      if (store.get().user?.id === session.user.id) {
        store.set({ profile, profileLoading: false });
      }
    })
    .catch((err) => {
      diag.record('profile-load-failed', err.message);
      if (store.get().user?.id === session.user.id) store.set({ profileLoading: false });
    })
    .finally(() => {
      if (profileInFlight === session.user.id) profileInFlight = null;
    });
}

function listenForAuthChanges(store) {
  gateway.onAuthChange((event, session) => {
    diag.record('auth-event', `${event}${session?.user ? ' (user)' : ' (no user)'}`);
    try {
      const sessionEvents = ['INITIAL_SESSION', 'SIGNED_IN', 'TOKEN_REFRESHED', 'USER_UPDATED'];
      if (event === 'PASSWORD_RECOVERY' && session?.user) {
        // The reset-email link signed them in — send them to set a new password
        applySession(store, session);
        store.set({ passwordRecovery: true });
      } else if (sessionEvents.includes(event) && session?.user) {
        applySession(store, session);
      } else if (event === 'SIGNED_OUT') {
        store.set({ user: null, profile: null, profileLoading: false });
        invalidate();
      }
    } catch (err) {
      diag.record('auth-event-error', err.message);
    }
  });
}

/**
 * Resume recovery (iPad Safari): after backgrounding, the SDK's in-memory
 * auth state can be lost while a valid session sits in localStorage —
 * restore the store's user without a reload. See docs/AUTH-AND-SDK.md.
 */
function setupResumeRecovery(store) {
  document.addEventListener('visibilitychange', async () => {
    if (document.visibilityState !== 'visible') return;
    if (store.get().user || !gateway.getStoredSession()) return;
    diag.record('resume-recovery', 'store user null but stored session exists');
    const session = await restoreSession();
    if (session?.user) applySession(store, session);
  });
}

/**
 * Initialize auth for the V2 app. Never throws and never blocks views:
 * callers fire-and-forget; the store broadcasts state as it settles.
 * @param {object} opts
 * @param {object} opts.store - The V2 store
 * @param {number} opts.sdkWaitMs - Per-attempt SDK wait (tests shrink this)
 */
export async function initAuth({ store, sdkWaitMs = 10000 }) {
  diag.setStatusProvider(() => {
    const user = store.get().user;
    const stored = gateway.getStoredSession();
    return {
      'store user': user ? user.email || user.id : 'null',
      'stored session': stored
        ? gateway.isSessionExpired(stored)
          ? 'expired'
          : 'valid'
        : 'none',
      sdk: sdkLoaded() ? 'loaded' : 'missing',
    };
  });
  diag.init();

  if (await ensureSdk(sdkWaitMs)) {
    // detectSessionInUrl in the client config consumes the OAuth redirect
    // (#access_token) and fires SIGNED_IN through this listener.
    gateway.init();
    listenForAuthChanges(store);
  } else {
    diag.record('sdk-load-failed', 'sign-in unavailable; stored session still honored');
  }

  try {
    const session = await restoreSession();
    if (session?.user) applySession(store, session);
  } catch (err) {
    diag.record('session-restore-failed', err.message);
  }

  store.set({ authReady: true });
  setupResumeRecovery(store);
}

// ---------------------------------------------------------------------------
// In-page Google sign-in (Google Identity Services → signInWithIdToken)
//
// The redirect flow opens accounts.google.com in an in-app Custom Tab when
// the site runs as an installed PWA on Android — and the passkey ceremony
// inside that tab can freeze forever (docs/AUTH-AUDIT-2026-08-21.md §1).
// GIS renders Google's own button; picking an account issues an ID token
// inside the app window, no navigation, no passkey step-up. The session it
// mints lands in the same localStorage the whole session design trusts.
//
// Requires YOINK_CONFIG.googleClientId (the same web client ID the Supabase
// Google provider uses, so the token's audience is already accepted; the
// site origin must be in that client's authorized JavaScript origins).
// Absent client ID, GIS load failure, or iOS → callers keep the proven
// redirect flow. iOS is excluded deliberately: the redirect works there,
// and GIS popups inside Home Screen apps do not.
// ---------------------------------------------------------------------------

const GSI_URL = 'https://accounts.google.com/gsi/client';

function isIos() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function gsiLoaded() {
  return Boolean(window.google?.accounts?.id);
}

function waitForGsi(timeoutMs) {
  return new Promise((resolve) => {
    if (gsiLoaded()) return resolve(true);
    const started = Date.now();
    const timer = setInterval(() => {
      if (gsiLoaded()) {
        clearInterval(timer);
        resolve(true);
      } else if (Date.now() - started >= timeoutMs) {
        clearInterval(timer);
        resolve(false);
      }
    }, 100);
  });
}

function loadGsi(timeoutMs = 5000) {
  if (!gsiLoaded() && !document.querySelector(`script[src="${GSI_URL}"]`)) {
    const script = document.createElement('script');
    script.src = GSI_URL;
    script.async = true;
    document.head.appendChild(script);
  }
  return waitForGsi(timeoutMs);
}

/**
 * Nonce pair for the token exchange: GIS gets the SHA-256 hash, Supabase
 * gets the raw value and verifies the hash in the token matches. Returns
 * nulls when Web Crypto is unavailable — Supabase then skips the check.
 */
async function generateNonce() {
  try {
    const raw = Array.from(crypto.getRandomValues(new Uint8Array(16)))
      .map((b) => b.toString(16).padStart(2, '0')).join('');
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(raw));
    const hashed = Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0')).join('');
    return { raw, hashed };
  } catch {
    return { raw: null, hashed: null };
  }
}

/**
 * Render Google's own sign-in button into `container` and wire the token
 * exchange. Resolves false when the in-page flow isn't available here
 * (no client ID, iOS, GIS or SDK failed to load) — the caller keeps the
 * redirect button. On a credential, the session is applied to the store
 * (which closes the auth dialog); failures go to opts.onError as
 * user-presentable text.
 * @param {HTMLElement} container - Empty element GIS renders into
 * @param {object} store - The V2 store
 * @param {object} [opts]
 * @param {(message: string) => void} [opts.onError]
 * @param {number} [opts.timeoutMs] - GIS script wait (tests shrink this)
 * @returns {Promise<boolean>} true if the button rendered
 */
export async function mountGoogleButton(container, store, opts = {}) {
  const clientId = window.YOINK_CONFIG?.googleClientId;
  if (!clientId || isIos()) return false;
  if (!(await loadGsi(opts.timeoutMs ?? 5000))) {
    diag.record('gsi-load-failed', 'falling back to redirect flow');
    return false;
  }
  // The exchange needs the Supabase SDK; without it the redirect button
  // (which reports its own SDK failure to the user) is the better door.
  if (!(await ensureSdk(opts.timeoutMs ?? 5000)) || !gateway.init()) return false;

  const nonce = await generateNonce();
  try {
    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: (response) => exchangeGoogleCredential(store, response?.credential, nonce.raw, opts.onError),
      nonce: nonce.hashed || undefined,
      auto_select: false,
      use_fedcm_for_prompt: true,
    });
    window.google.accounts.id.renderButton(container, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'continue_with',
      width: 280,
    });
  } catch (err) {
    diag.record('gsi-init-failed', err.message);
    return false;
  }
  const mounted = container.childElementCount > 0;
  diag.record('gsi-button', mounted ? 'mounted' : 'render produced nothing');
  return mounted;
}

async function exchangeGoogleCredential(store, credential, nonce, onError) {
  const fail = (detail) => {
    diag.record('gsi-exchange-failed', detail);
    if (onError) onError('Google sign-in failed. Please try again.');
  };
  if (!credential) return fail('no credential in GIS response');
  try {
    const { data, error } = await gateway.signInWithIdToken('google', credential, nonce || undefined);
    if (error) return fail(error.message);
    if (!data?.session?.user) return fail('no session in exchange response');
    diag.record('gsi-signin', 'session established');
    applySession(store, data.session);
  } catch (err) {
    fail(err.message);
  }
}

/**
 * Start the Google OAuth redirect flow. Returns the user to the page they
 * were on, query string included — dropping it would strip ?authdiag=1
 * (and any cache-busting param) from the round trip, and the return URL
 * must land on a page that actually boots the app to consume the token.
 * Throws with a user-presentable message on failure.
 */
export async function signInWithGoogle() {
  if (!(await ensureSdk(5000)) || !gateway.init()) {
    throw new Error('Sign-in is unavailable right now — the auth library could not load.');
  }
  diag.record('oauth-start', 'google');
  const redirectTo = window.location.origin + window.location.pathname + window.location.search;
  const { error } = await gateway.signInWithOAuth('google', redirectTo);
  if (error) throw error;
  // The browser navigates away to Google from here.
}

/**
 * Email + password sign-in (admin accounts and anyone without Google).
 * Applies the session to the store directly so the UI updates even if
 * the SDK's SIGNED_IN event is unreliable (iPad). Throws on failure
 * with a user-presentable message.
 */
export async function signInWithEmail(store, email, password) {
  if (!(await ensureSdk(5000)) || !gateway.init()) {
    throw new Error('Sign-in is unavailable right now — the auth library could not load.');
  }
  diag.record('email-signin', 'attempt');
  const { data, error } = await gateway.signIn(email, password);
  if (error) {
    diag.record('email-signin', `failed: ${error.message}`);
    const msg = /invalid login credentials/i.test(error.message || '')
      ? 'Email or password didn’t match.'
      : error.message || 'Sign-in failed. Please try again.';
    throw new Error(msg);
  }
  if (!data?.session?.user) throw new Error('Sign-in failed. Please try again.');
  applySession(store, data.session);
  return data.session;
}

/**
 * Email + password + username sign-UP — V1's handleEmailSignUp
 * (auth.js:76-172) whole: password rules, username availability
 * pre-check (case-insensitive, so the opaque "Database error saving new
 * user" from the unique-username trigger rarely surfaces), the
 * email-confirmation-required branch, and Supabase-error translation.
 * `extra` carries the optional { signupSource, signupSourceDetail } answer
 * from the form's "How did you hear about us?" field.
 * Returns { confirmationRequired } — true means "check your email".
 */
export async function signUpWithEmail(store, email, password, username, extra = {}) {
  if (!(await ensureSdk(5000)) || !gateway.init()) {
    throw new Error('Sign-up is unavailable right now — the auth library could not load.');
  }
  const passwordCheck = validatePassword(password);
  if (!passwordCheck.valid) throw new Error(passwordCheck.message);
  const rawUsername = (username || '').trim();
  if (!rawUsername) throw new Error('Please enter a username');

  diag.record('email-signup', 'attempt');
  try {
    const { data: existing } = await gateway.rpc('get_public_profile', { p_username: rawUsername });
    if (Array.isArray(existing) ? existing.length > 0 : existing) {
      throw new Error(
        'That username is already taken. If this is your account, try signing in instead — otherwise pick a different username.'
      );
    }
  } catch (err) {
    if (/already taken/.test(err.message)) throw err;
    // Transient check failure must not block signup (V1 behavior)
    diag.record('email-signup', `username pre-check failed, continuing: ${err.message}`);
  }

  // "How did you hear about us?" rides along as signup metadata; the
  // handle_new_user trigger copies it onto the profile row as it is
  // created. Keys are only sent when answered, so the metadata stays
  // minimal for the person who skipped it.
  const metadata = { username: rawUsername };
  if (extra.signupSource) {
    metadata.signup_source = extra.signupSource;
    if (extra.signupSourceDetail) metadata.signup_source_detail = extra.signupSourceDetail;
  }
  const { data, error } = await gateway.signUp(email, password, metadata);
  if (error) {
    diag.record('email-signup', `failed: ${error.message}`);
    const msg = error.message || '';
    if (/sending confirmation email|send email/i.test(msg)) {
      throw new Error('Unable to send confirmation email. Please check your email address and try again, or try signing up with Google.');
    }
    if (/already registered|already been registered/i.test(msg)) {
      throw new Error('This email is already registered. Try signing in instead.');
    }
    if (/rate limit|too many requests/i.test(msg)) {
      throw new Error('Too many signup attempts. Please wait a few minutes and try again.');
    }
    if (/Database error saving new user|unexpected_failure/i.test(msg)) {
      throw new Error('Could not create your account. The username or email may already be in use — try a different username or sign in instead.');
    }
    throw new Error(msg || 'Sign up failed. Please try again.');
  }
  if (data?.user && data.user.identities?.length === 0) {
    // Email confirmation required before the session exists
    return { confirmationRequired: true };
  }
  if (data?.session?.user) applySession(store, data.session);
  return { confirmationRequired: false };
}

/**
 * Sign out. The SDK call is raced against a timeout and storage is
 * cleared regardless, so logout works even when the SDK is wedged
 * (the iPad navigator.locks failure mode).
 */
export async function signOutUser(store) {
  diag.record('sign-out', 'requested');
  try {
    const result = await Promise.race([
      gateway.signOut(),
      new Promise((_, reject) => setTimeout(() => reject(new Error('signOut timed out')), 5000)),
    ]);
    if (result?.error) diag.record('sign-out', `SDK error: ${result.error.message}`);
  } catch (err) {
    diag.record('sign-out', `SDK signOut failed (${err.message}); clearing storage anyway`);
  }
  gateway.clearStoredSession();
  store.set({ user: null, profile: null, profileLoading: false });
  invalidate();
}

/**
 * Send the password-reset email (the link returns to the app and fires
 * PASSWORD_RECOVERY, which routes to the account page).
 */
export async function requestPasswordReset(email) {
  if (!(await ensureSdk(5000)) || !gateway.init()) {
    throw new Error('Reset is unavailable right now — the auth library could not load.');
  }
  const basePath = window.YOINK_CONFIG?.basePath || '/adventure';
  const { error } = await gateway.resetPassword(email, window.location.origin + basePath + '/');
  if (error) {
    // V1's friendly translations (auth.js:203-216) — raw Supabase
    // rate-limit strings shouldn't reach the user
    const msg = error.message || '';
    if (/sending recovery|send email/i.test(msg)) {
      throw new Error('Unable to send reset email right now. Please wait a few minutes and try again, or try signing in with Google.');
    }
    if (/rate limit|security purposes|too many requests/i.test(msg)) {
      throw new Error('Too many reset attempts. Please wait a few minutes and try again.');
    }
    throw error;
  }
}
